import React, { useState, useEffect } from 'react';
import { CollaborationTask } from '../../data/initialContactsData';
import { getTasks, addTask, updateTaskStatus, getContacts } from '../../utils/portalStorage';

const STATUSES: Array<CollaborationTask['status']> = ['To Do', 'In Progress', 'Under Review', 'Completed'];

export const CollaborationBoard: React.FC = () => {
  const [tasks, setTasks] = useState<CollaborationTask[]>([]);
  const [isNewTaskOpen, setIsNewTaskOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // New Task state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<CollaborationTask['category']>('Logistics');
  const [priority, setPriority] = useState<CollaborationTask['priority']>('High');
  const [assigneeName, setAssigneeName] = useState('Engr. Fidelis Afegbua');
  const [assigneeVillage, setAssigneeVillage] = useState('Afashio-Uzairue');
  const [dueDate, setDueDate] = useState('2026-11-20');

  const contacts = getContacts();

  const loadTasks = () => {
    setTasks(getTasks());
  };

  useEffect(() => {
    loadTasks();
  }, []);

  const handleCreateTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    addTask({
      title: title.trim(),
      description: description.trim(),
      category,
      status: 'To Do',
      priority,
      assigneeName,
      assigneeVillage,
      dueDate,
    });

    setTitle('');
    setDescription('');
    setIsNewTaskOpen(false);
    loadTasks();
  };

  const handleAdvanceStatus = (taskId: string, currentStatus: CollaborationTask['status']) => {
    const idx = STATUSES.indexOf(currentStatus);
    if (idx < STATUSES.length - 1) {
      updateTaskStatus(taskId, STATUSES[idx + 1]);
      loadTasks();
    }
  };

  const filteredTasks = tasks.filter((t) => selectedCategory === 'all' || t.category === selectedCategory);

  return (
    <div className="space-y-6">
      {/* Board Header */}
      <div className="bg-slate-900 border border-slate-800 p-5 rounded-2xl shadow-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 text-white">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-black font-serif text-amber-300 uppercase tracking-wide">
              Festival Collaboration & Project Notice Board
            </h2>
            <span className="bg-emerald-900 text-emerald-300 text-[10px] px-2 py-0.5 rounded-full font-mono font-bold">
              {tasks.length} Active Festival Tasks
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Track palace grandstand preparations, cultural rehearsals, sponsorship, and ground logistics
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-slate-950 border border-slate-700 text-amber-300 text-xs px-3 py-2 rounded-xl focus:outline-none cursor-pointer"
          >
            <option value="all">All Task Categories</option>
            <option value="Logistics">Logistics</option>
            <option value="Cultural">Cultural</option>
            <option value="Fundraising">Fundraising</option>
            <option value="Media & PR">Media & PR</option>
            <option value="Protocol & Security">Protocol & Security</option>
          </select>

          <button
            onClick={() => setIsNewTaskOpen(true)}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs px-4 py-2 rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1.5"
          >
            <span>➕</span> Create Festival Task
          </button>
        </div>
      </div>

      {/* Kanban Board Columns */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {STATUSES.map((status) => {
          const colTasks = filteredTasks.filter((t) => t.status === status);
          return (
            <div key={status} className="bg-slate-950/80 border border-slate-800 p-4 rounded-2xl space-y-3 flex flex-col">
              <div className="flex justify-between items-center border-b border-slate-800 pb-2.5">
                <h3 className="font-serif text-amber-300 font-bold text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <span>
                    {status === 'To Do'
                      ? '📌'
                      : status === 'In Progress'
                      ? '⚡'
                      : status === 'Under Review'
                      ? '👁️'
                      : '✅'}
                  </span>
                  {status}
                </h3>
                <span className="bg-slate-800 text-slate-300 text-[10px] px-2 py-0.5 rounded-full font-mono font-bold">
                  {colTasks.length}
                </span>
              </div>

              <div className="space-y-3 flex-1 overflow-y-auto max-h-[500px] pr-1">
                {colTasks.length === 0 ? (
                  <div className="text-center py-8 text-slate-600 text-[11px] italic">No tasks in this lane</div>
                ) : (
                  colTasks.map((task) => (
                    <div
                      key={task.id}
                      className="bg-slate-900 border border-slate-800 hover:border-amber-500/40 p-4 rounded-xl space-y-2.5 shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="bg-slate-950 text-amber-300 text-[9px] px-2 py-0.5 rounded font-mono uppercase border border-amber-500/30">
                          {task.category}
                        </span>
                        <span
                          className={`text-[9px] px-1.5 py-0.5 rounded font-extrabold uppercase ${
                            task.priority === 'High'
                              ? 'bg-red-950 text-red-300 border border-red-700/50'
                              : 'bg-slate-800 text-slate-400'
                          }`}
                        >
                          {task.priority}
                        </span>
                      </div>

                      <h4 className="font-bold text-white text-xs leading-snug">{task.title}</h4>
                      <p className="text-[11px] text-slate-400 line-clamp-2">{task.description}</p>

                      <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px] text-slate-400">
                        <div>
                          <strong className="text-emerald-300">{task.assigneeName}</strong>
                          <span className="text-slate-500 ml-1">({task.assigneeVillage})</span>
                        </div>
                        <div className="font-mono text-slate-500">Due: {task.dueDate}</div>
                      </div>

                      {status !== 'Completed' && (
                        <button
                          onClick={() => handleAdvanceStatus(task.id, task.status)}
                          className="w-full mt-1 py-1.5 bg-slate-800 hover:bg-slate-700 text-amber-300 font-bold text-[10px] rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1"
                        >
                          <span>Move to Next Phase</span> ➔
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Task Modal */}
      {isNewTaskOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 text-white">
          <div className="bg-slate-900 border border-amber-500/40 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-slate-950 border-b border-slate-800 p-4 flex justify-between items-center">
              <h3 className="font-serif text-amber-300 font-bold text-sm uppercase">Create Festival Collaboration Task</h3>
              <button onClick={() => setIsNewTaskOpen(false)} className="text-slate-400 hover:text-white">
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateTask} className="p-5 space-y-3 text-xs">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Task Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. VIP Royal Throne Sound System Setup"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Description / Deliverables</label>
                <textarea
                  rows={3}
                  placeholder="Provide scope, venue, and specific requirements"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as any)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-amber-300"
                  >
                    <option value="Logistics">Logistics</option>
                    <option value="Cultural">Cultural</option>
                    <option value="Fundraising">Fundraising</option>
                    <option value="Media & PR">Media & PR</option>
                    <option value="Protocol & Security">Protocol & Security</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-300 font-bold mb-1">Priority</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as any)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-amber-300"
                  >
                    <option value="High">High Priority</option>
                    <option value="Medium">Medium Priority</option>
                    <option value="Low">Low Priority</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Assignee from Directory</label>
                <select
                  value={assigneeName}
                  onChange={(e) => {
                    const selected = contacts.find((c) => c.fullName === e.target.value);
                    setAssigneeName(e.target.value);
                    if (selected) setAssigneeVillage(selected.village);
                  }}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-amber-300"
                >
                  {contacts.map((c) => (
                    <option key={c.id} value={c.fullName}>
                      {c.fullName} ({c.village})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Due Date</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white"
                />
              </div>

              <div className="pt-3 border-t border-slate-800 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsNewTaskOpen(false)}
                  className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg"
                >
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-amber-500 text-slate-950 font-bold rounded-lg shadow">
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaborationBoard;
